// Converts agenthub's persisted thread messages into ThreadMessageLike[].
// Live/streaming state is owned by AgentHub's external assistant-ui runtime;
// this file's job is the persisted-history half.

import type { MessageTiming, ThreadMessage, ThreadMessageLike } from '@assistant-ui/react';
import { fromThreadMessageLike } from '@assistant-ui/react';
import type { ThreadMessageRecordView } from './ChatRuntime';
import type { ThreadToolCallView } from '@/entities/thread/model/types';
import type { ChartArtifact, UiArtifact } from '@/entities/chat/model/types';
import { artifactDownloadUrl, isImageContentType, isImageFile } from '@/entities/file';
import { readEnv } from '@/shared/config';

type ReadonlyJSONValue =
  | string
  | number
  | boolean
  | null
  | ReadonlyJSONObject
  | readonly ReadonlyJSONValue[];
type ReadonlyJSONObject = { readonly [key: string]: ReadonlyJSONValue };

// ThreadMessageLike['content'] is `string | readonly Part[]` — Extract the array
// branch first so indexing with [number] yields the real Part union, not
// `string | Part` (indexing the raw union picks up string's index signature too).
type ThreadMessagePart = Extract<ThreadMessageLike['content'], readonly unknown[]>[number];

// Tool-call args come off the wire as Record<string, unknown>, which doesn't
// structurally satisfy assistant-ui's stricter ReadonlyJSONObject value type.
// A JSON round-trip both proves and enforces JSON-safety in one step, rather
// than a blind type cast.
function toJsonArgs(args: Record<string, unknown> | undefined): ReadonlyJSONObject {
  return args ? (JSON.parse(JSON.stringify(args)) as ReadonlyJSONObject) : {};
}

// Compatibility-only adapter for historical closed chart records. New Claude
// runs use the MCP artifact contract and emit VegaLiteChart generative UI.
export function chartArtifactPart(chart: ChartArtifact): ThreadMessagePart {
  const valueField = chart.series[0]?.name ?? 'value';
  const mark = chart.type === 'pie' ? 'arc' : chart.type === 'area' ? 'area' : chart.type === 'line' ? 'line' : chart.type === 'scatter' ? 'point' : 'bar';
  const spec = chart.type === 'pie'
    ? {
        $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
        data: { values: chart.data },
        mark,
        encoding: {
          theta: { field: valueField, type: 'quantitative' },
          color: { field: 'label', type: 'nominal' },
        },
      }
    : {
        $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
        data: { values: chart.data },
        mark,
        encoding: {
          x: { field: 'label', type: 'nominal' },
          y: { field: valueField, type: 'quantitative' },
        },
      };
  return {
    type: 'generative-ui',
    spec: { root: [{ component: 'VegaLiteChart', props: { spec } }] },
  };
}

// Vega-Lite/Mermaid/Svg — open-ended generative-UI artifacts parsed from
// fenced blocks (see backend services/chat/ui_artifacts.py). `props` is
// passed straight through to the matching renderer registered in
// ChatMessage.tsx's `generativeUiComponents` map.
export function uiArtifactPart(artifact: UiArtifact): ThreadMessagePart {
  return {
    type: 'generative-ui',
    spec: { root: [{ component: artifact.component, props: artifact.props }] },
  };
}

function absoluteImageUrl(url: string): string {
  if (/^(?:https?:|data:|blob:)/i.test(url)) return url;
  const origin = globalThis.location?.origin ?? 'http://localhost';
  // A preview's API base can itself be relative (`/preview/pr-N/api`).
  const apiBase = new URL(readEnv('VITE_API_BASE_URL') ?? origin, origin);
  return new URL(url, apiBase).toString();
}

const TRANSPARENT_IMAGE_DATA_URI =
  'data:image/gif;base64,R0lGODlhAQABAAAAACw=';

function isAssistantUiImageUrl(url: string): boolean {
  return /^(?:https:\/\/|blob:|data:image\/(?:png|jpeg|jpg|gif|webp|svg\+xml);base64,)/i.test(url);
}

function isInternalArtifactDownloadUrl(url: string): boolean {
  try {
    return /\/api\/v1\/artifacts\/[^/]+\/download\/?$/.test(new URL(url).pathname);
  } catch {
    return /\/api\/v1\/artifacts\/[^/]+\/download\/?$/.test(url);
  }
}

export function imageArtifactPart(artifact: NonNullable<ThreadMessageRecordView['artifacts']>[number]): ThreadMessagePart {
  const imageUrl = absoluteImageUrl(artifact.downloadUrl ?? artifactDownloadUrl('', artifact.artifactId));
  if (!isAssistantUiImageUrl(imageUrl) || isInternalArtifactDownloadUrl(imageUrl)) {
    return {
      type: 'image',
      image: TRANSPARENT_IMAGE_DATA_URI,
      filename: artifact.filename,
      providerMetadata: {
        agenthub: {
          artifactUrl: imageUrl,
          artifactId: artifact.artifactId,
          contentType: artifact.contentType,
          sizeBytes: artifact.sizeBytes,
        },
      },
    } as ThreadMessagePart;
  }
  return {
    type: 'image',
    image: imageUrl,
    filename: artifact.filename,
  };
}

export function fileArtifactPart(file: {
  fileId?: string;
  artifactId?: string;
  filename: string;
  contentType?: string | null;
  sizeBytes?: number | null;
  downloadUrl?: string | null;
}): ThreadMessagePart {
  const id = file.fileId ?? file.artifactId;
  const url = file.downloadUrl ?? (id ? artifactDownloadUrl('', id) : '');
  return {
    type: 'file',
    data: absoluteImageUrl(url),
    filename: file.filename,
    mimeType: file.contentType ?? undefined,
    providerMetadata: {
      agenthub: {
        artifactId: id,
        sizeBytes: file.sizeBytes,
      },
    },
  } as ThreadMessagePart;
}

/**
 * A specialist's tool calls persist as flat siblings of the `Agent:<name>`
 * card that made them, linked only by the delegated agent id the collector
 * writes into `args._argus`. Regrouping them here is what makes a reloaded
 * thread show the same nested cards the live stream showed.
 */
export function delegatedAgentId(args: Record<string, unknown>): string | null {
  const argus = args._argus;
  if (typeof argus !== 'object' || argus === null) return null;
  const id = (argus as { delegated_agent_id?: unknown }).delegated_agent_id;
  return typeof id === 'string' && id ? id : null;
}

function persistedToolCallPart(call: ThreadToolCallView, toolCallId: string): ThreadMessagePart {
  return {
    type: 'tool-call',
    toolCallId,
    toolName: call.tool,
    args: toJsonArgs(call.args),
    argsText: JSON.stringify(call.args ?? {}),
    result: call.result,
  };
}

function specialistCardMessages(
  card: ThreadToolCallView,
  cardToolCallId: string,
  children: Array<{ call: ThreadToolCallView; toolCallId: string }>,
  artifacts: NonNullable<ThreadMessageRecordView['artifacts']>,
): ThreadMessage[] {
  const messages: ThreadMessage[] = [];
  const task = card.args.task;
  if (typeof task === 'string' && task) {
    messages.push(fromThreadMessageLike(
      { id: `${cardToolCallId}-prompt`, role: 'user', content: [{ type: 'text', text: task }] },
      `${cardToolCallId}-prompt`,
      { type: 'complete', reason: 'stop' },
    ));
  }
  const result = card.result;
  const answer = typeof result === 'string'
    ? result
    : typeof (result as { text?: unknown } | null)?.text === 'string' ? (result as { text: string }).text : '';
  const content: ThreadMessagePart[] = children.map(({ call, toolCallId }) => persistedToolCallPart(call, toolCallId));
  if (answer) content.push({ type: 'text', text: answer });
  // A specialist's own generated files -- reload must nest these inside its
  // card, same as the live stream does, instead of dropping them at the top
  // level of Argus's own message where they'd read as Argus's own output.
  artifacts.forEach((artifact) => {
    content.push(isImageContentType(artifact.contentType) ? imageArtifactPart(artifact) : fileArtifactPart(artifact));
  });
  messages.push(fromThreadMessageLike(
    { id: `${cardToolCallId}-response`, role: 'assistant', content },
    `${cardToolCallId}-response`,
    { type: 'complete', reason: 'stop' },
  ));
  return messages;
}

type PersistedAttachment = NonNullable<ThreadMessageLike['attachments']>[number];

// A user's uploads rehydrate as that message's attachments — the shape the
// composer sent them in — so they render above the bubble (images as
// thumbnails) instead of as user file parts nothing displays.
function uploadedFileAttachment(
  file: NonNullable<ThreadMessageRecordView['uploadedFiles']>[number],
  fallbackId: string,
): PersistedAttachment {
  return {
    id: file.fileId ?? fallbackId,
    type: isImageFile(file.contentType, file.filename) ? 'image' : 'file',
    name: file.filename,
    contentType: file.contentType,
    status: { type: 'complete' },
    content: [fileArtifactPart(file) as PersistedAttachment['content'][number]],
  };
}

/**
 * Convert one persisted message record into a ThreadMessageLike. Pure — no
 * React or network calls — safe to unit test standalone.
 */
export function toPersistedMessageLike(record: ThreadMessageRecordView): ThreadMessageLike {
  const role = record.role === 'user' ? 'user' : 'assistant';
  const content: ThreadMessagePart[] = [];

  if (role === 'assistant' && record.orchestrationConsults && record.orchestrationConsults.length > 0) {
    content.push({
      type: 'data',
      name: 'specialist_selection',
      data: { consults: record.orchestrationConsults },
    });
  } else if (role === 'assistant' && record.orchestrationPreamble) {
    content.push({
      type: 'data',
      name: 'claude_narration',
      data: { text: record.orchestrationPreamble },
    });
  }

  if (role === 'assistant' && record.thinking) {
    content.push({ type: 'reasoning', text: record.thinking });
  }

  const persistedCalls = (record.toolCalls ?? []).map((call, index) => ({
    call,
    // Fall back to a positional id for rows written before the stream id was
    // exposed; matching by id is only needed to keep an open card open.
    toolCallId: call.toolCallId ?? `${record.id}-tool-${index}`,
    agentId: delegatedAgentId(call.args),
    // Only Argus's outer consults are cards. A specialist can delegate to an
    // SDK sub-agent too; its `Agent:sub-agent` tool must remain nested in the
    // owning specialist card after a session reload.
    isCard: call.tool.startsWith('Agent:') && (call.toolCallId ?? '').startsWith('argus-delegate-'),
  }));
  const cardAgentIds = new Set(persistedCalls.filter((item) => item.isCard && item.agentId).map((item) => item.agentId));
  // A specialist's generated files are tagged with the same delegated agent
  // id its tool calls carry (see services/artifacts.py's ArtifactDescriptor)
  // -- route them into that specialist's card below instead of the top level.
  const cardArtifacts = (record.artifacts ?? []).filter(
    (artifact) => artifact.delegatedAgentId && cardAgentIds.has(artifact.delegatedAgentId),
  );

  persistedCalls.forEach((item) => {
    if (item.isCard) {
      const children = persistedCalls.filter((candidate) => !candidate.isCard && candidate.agentId === item.agentId);
      const artifacts = cardArtifacts.filter((artifact) => artifact.delegatedAgentId === item.agentId);
      content.push({
        ...persistedToolCallPart(item.call, item.toolCallId),
        messages: specialistCardMessages(item.call, item.toolCallId, children, artifacts),
      } as ThreadMessagePart);
      return;
    }
    // Nested inside its card above, not repeated at the top level.
    if (item.agentId && cardAgentIds.has(item.agentId)) return;
    content.push(persistedToolCallPart(item.call, item.toolCallId));
  });

  // Same data part the live `connect_required` metadata event produces, so the
  // Connect banner survives hydration (a new chat's first turn remounts from
  // persisted history, as does a reload).
  if (role === 'assistant') {
    (record.connectRequired ?? []).forEach((provider) => {
      content.push({ type: 'data', name: 'connect_required', data: { provider } });
    });
  }

  (record.skillUses ?? []).forEach((skill) => {
    content.push({
      type: 'tool-call',
      toolCallId: skill.toolCallId ?? `${record.id}-skill-${skill.skillName}`,
      toolName: 'Skill',
      args: toJsonArgs(skill.args ?? { skill: skill.skillName }),
      argsText: JSON.stringify(skill.args ?? { skill: skill.skillName }),
    });
  });

  // format defaults to 'markdown' when absent — only 'html'/'text' need a marker;
  // MarkdownText reads this to pick text/markdown/html rendering (fail-closed:
  // an unrecognized format falls back to plain text, never raw HTML by default).
  content.push({
    type: 'text',
    text: record.content,
    ...(record.format === 'html' || record.format === 'text'
      ? { providerMetadata: { agenthub: { format: record.format } } }
      : {}),
  });

  (record.chartArtifacts ?? []).forEach((chart) => content.push(chartArtifactPart(chart)));
  (record.uiArtifacts ?? []).forEach((artifact) => content.push(uiArtifactPart(artifact)));

  // fromThreadMessageLike() rejects attachments on non-user messages.
  const attachments = role === 'user'
    ? (record.uploadedFiles ?? []).map((file, index) => uploadedFileAttachment(file, `${record.id}-upload-${index}`))
    : [];
  if (role !== 'user') {
    (record.uploadedFiles ?? []).forEach((file) => {
      content.push(fileArtifactPart(file));
    });
  }

  // Files the agent generated this run use assistant-ui's native file/image
  // parts. The backend remains the only authority for the download URL.
  // Also pushed into the owning specialist's card above (cardArtifacts) --
  // a specialist's card can be, and usually is, collapsed by default, so its
  // download link must surface here too rather than only inside the card.
  (record.artifacts ?? []).forEach((artifact) => {
    if (isImageContentType(artifact.contentType)) {
      content.push(imageArtifactPart(artifact));
      return;
    }
    content.push(fileArtifactPart(artifact));
  });

  {
    // NOT a reconstruction of live stream order: ThreadMessageRecordView
    // persists content/thinking/toolCalls/chartArtifacts/uiArtifacts as
    // separate fields, so the true interleaving (e.g. reasoning -> tool call
    // -> reasoning -> tool call) is lost by the time it reaches this
    // function — there is no per-part sequence to sort by. This is a fixed
    // canonical grouping applied on every refresh, so a refreshed thread at
    // least renders consistently with itself; it will not match the order
    // the same turn streamed in live for any turn with real interleaving.
    // A true fix needs the persisted schema to keep one ordered part
    // sequence instead of per-type fields.
    const reasoning = content.filter((part) => part.type === 'reasoning');
    const tools = content.filter((part) => part.type === 'tool-call');
    const ui = content.filter((part) => part.type === 'generative-ui');
    const narration = content.filter((part) => part.type === 'data' && (part.name === 'claude_narration' || part.name === 'orchestration_preamble' || part.name === 'specialist_selection'));
    const text = content.filter((part) => part.type === 'text');
    const files = content.filter((part) => part.type === 'file');
    const groupedTypes = new Set(['reasoning', 'tool-call', 'generative-ui', 'file', 'text']);
    const other = content.filter((part) => part.type !== 'data' || !narration.includes(part as typeof narration[number]))
      .filter((part) => !groupedTypes.has(part.type));
    content.splice(0, content.length, ...reasoning, ...tools, ...ui, ...files, ...narration, ...text, ...other);
  }

  // Rebuild persisted timing from the same fields the debug panel already
  // reads (executionTimeMs/toolCalls/outputTokens) so the hover badge in
  // ActionBar.tsx (`useMessageTiming()`) has real numbers after a refetch.
  const timing: MessageTiming | undefined =
    role === 'assistant' && record.executionTimeMs != null && record.executionTimeMs > 0
      ? {
          streamStartTime: (record.createdAt ? new Date(record.createdAt).getTime() : Date.now()) - record.executionTimeMs,
          totalStreamTime: record.executionTimeMs,
          // ponytail: no per-chunk history is persisted server-side, so this
          // counts tool calls + the final text blob rather than real chunks.
          totalChunks: (record.toolCalls?.length ?? 0) + 1,
          toolCallCount: record.toolCalls?.length ?? 0,
          ...(record.outputTokens != null && {
            tokenCount: record.outputTokens,
            tokensPerSecond: record.outputTokens / (record.executionTimeMs / 1000),
          }),
        }
      : undefined;

  return {
    role,
    id: record.id,
    createdAt: record.createdAt ? new Date(record.createdAt) : undefined,
    content,
    ...(attachments.length > 0 ? { attachments } : {}),
    // `status` is only valid on assistant messages — fromThreadMessageLike()
    // throws "status is only supported for assistant messages" otherwise,
    // which previously killed history.load() for every thread (any user
    // message present made the whole conversion reject silently).
    ...(role === 'assistant'
      ? {
          status: record.timedOut
            ? ({ type: 'incomplete', reason: 'other' } as const)
            : ({ type: 'complete', reason: 'stop' } as const),
        }
      : {}),
    metadata: {
      ...(timing ? { timing } : {}),
      custom: {
        runId: record.runId ?? undefined,
        threadId: record.threadId ?? undefined,
        requestId: record.requestId ?? undefined,
        executionTimeMs: record.executionTimeMs ?? undefined,
        turnsUsed: record.turnsUsed ?? undefined,
        rawRequest: record.rawRequest ?? undefined,
        rawResponse: record.rawResponse ?? undefined,
        sourceLabel: record.sourceLabel ?? undefined,
        routingDecision: record.routingDecision ?? undefined,
        timedOut: record.timedOut ?? undefined,
        runSource: record.runSource ?? undefined,
        modelName: record.modelName ?? undefined,
        inputTokens: record.inputTokens ?? undefined,
        outputTokens: record.outputTokens ?? undefined,
        cacheReadInputTokens: record.cacheReadInputTokens ?? undefined,
        cacheCreationInputTokens: record.cacheCreationInputTokens ?? undefined,
        contextWindow: record.contextWindow ?? undefined,
        // `useMessageQuote`/`MessagePrimitive.Quote` read `metadata.custom.quote` —
        // only ever set on persisted user messages (see services/sessions.py).
        ...(role === 'user' && record.quote ? { quote: record.quote } : {}),
      },
    },
  };
}
